import React, { useState } from 'react';
import HealthDataVerticalTable from '../../../HealthData/Components/Tables/HealthDataVerticalTable';
import { Table } from '../../../HealthData/Enums/Table.enum';
import { IHealthData } from '../../../HealthData/Hooks/useHealthData';
import moment from 'moment';
import OverlayLoading from '../../../../Components/OverlayLoading';
import useTranslation from '../../../../Hooks/useTranslation';
import useQuestionnaireResultTable from '../../Hooks/useQuestionnaireResultTable';
import { useEffectAsync } from '../../../../helpers';
import { flatten } from 'lodash';
import { Questionnaire } from '../../Enums/Questionnaire.enum';
import HealthDataChart from '../../../HealthData/Components/Charts/HealthDataChart';
import { Chart } from '../../../HealthData/Enums/Chart.enum';
import Text from '../../../../Components/Base/Text/Text';
import PatientMiniProfile from '../../../../Components/PatientMiniProfile/PatientMiniProfile';
import { useAppContext } from '../../../../Services/AppContext';
import QuestionnaireExportButton from '../QuestionnaireExport/QuestionnaireExportButton';
// @ts-ignore
import styles from './QuestionnaireResults.module.scss';

export interface MonitoringDataset {
	results: IHealthData[];
}

interface Props {
	patientId: string;
	questionnaireCode: string;
	questionnaireName: string;
	roundValuesTo?: number;
}

const QuestionnaireResults: React.FC<Props> = ({
	patientId,
	questionnaireCode,
	questionnaireName,
	roundValuesTo,
}) => {
	const [dataset, setDataset] = useState<any>({ results: [] });
	const [isLoading, setIsLoading] = useState(false);
	const { t } = useTranslation();
	const { getDataAsHealthData } = useQuestionnaireResultTable();
	const { workplaceId, userData } = useAppContext();

	useEffectAsync(async () => {
		setIsLoading(true);
		setDataset({
			results: flatten(
				Object.values(
					await getDataAsHealthData({
						patientId,
						questionnaireCode,
						questionnaireName,
						t,
						roundValuesTo,
					}),
				),
			),
		} as MonitoringDataset);
		setIsLoading(false);
	}, []);

	if (!dataset?.results.length) {
		return (
			<Text tx='component.questionnaireResults.patientHaveNotFilledQuestionnaire' />
		);
	}

	return (
		<>
			{isLoading && <OverlayLoading />}
			<div>
				{dataset?.results && dataset.results.length > 0 && (
					<>
						<PatientMiniProfile
							userId={patientId}
							workplaceId={workplaceId}
							loggedUserId={userData.id}
							showVerifyInformation={false}
							links={null}
							showAge
							showBirthDate
							showAddress
							showPhone={false}
							showEmail={false}
							showInsuranceCompany
							showGender={false}
							allowOpenProfile={false}
						/>
						<div className={styles.exportWrapper}>
							<QuestionnaireExportButton
								questionnaireCode={questionnaireCode}
								patientId={patientId}
							/>
						</div>
						<HealthDataVerticalTable
							tableName={Table.QUESTIONNAIRE_RESULTS}
							tableTitle={questionnaireName}
							healthData={dataset.results}
							isLoading={isLoading}
							filter={{
								from: moment(),
								to: moment(),
								view: 'month',
								source: [],
								keys: [],
								type: 'raw',
							}}
						/>
						{questionnaireCode === Questionnaire.ESAS && (
							<HealthDataChart
								patientId={patientId}
								filter={{
									from: moment(),
									to: moment(),
									view: 'month',
									source: [],
									keys: [],
									type: 'raw',
								}}
								isLoading={isLoading}
								healthData={dataset.results}
								chartType={Chart.ESAS}
							/>
						)}
					</>
				)}
			</div>
		</>
	);
};

export default QuestionnaireResults;
